import './primary-cta.scss';

import {
  type HTMLAttributes,
  type PropsWithChildren,
  type ReactElement,
  createContext,
  use,
  useId,
} from 'react';

import type {
  Pictogram,
  PictogramProps,
} from '@sola/ark-core/atoms/pictogram/pictogram.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';

import type {
  CommitmentAvatar,
  CommitmentAvatarProps,
} from '../../atoms/commitment-avatar/commitment-avatar.tsx';

export type PrimaryCtaVariant = 'default' | 'positive' | 'neutral';
export type PrimaryCtaSize = 'sm' | 'md';

type PrimaryCtaContextValue = {
  headingId: string;
  size: PrimaryCtaSize;
};

const PrimaryCtaContext = createContext<PrimaryCtaContextValue | undefined>(
  undefined,
);

const usePrimaryCtaContext = () => {
  const context = use(PrimaryCtaContext);

  if (!context) {
    throw new Error(
      'PrimaryCta compound components must be rendered within PrimaryCta.',
    );
  }

  return context;
};

export type PrimaryCtaProps = PropsWithChildren<
  Omit<HTMLAttributes<HTMLDivElement>, 'children'> & {
    variant?: PrimaryCtaVariant;
    fullWidth?: boolean;
    size?: PrimaryCtaSize;
  }
>;

export const PrimaryCta: React.FC<PrimaryCtaProps> = ({
  variant = 'default',
  fullWidth = false,
  size = 'md',
  className,
  children,
  ...props
}) => {
  const headingId = useId();

  return (
    <PrimaryCtaContext value={{ headingId, size }}>
      <div
        {...props}
        role="group"
        aria-labelledby={headingId}
        className={cn(
          'PrimaryCta flex flex-col md:flex-row md:items-center rounded-md p-comfy-sm gap-comfy-xs md:gap-comfy-sm',
          fullWidth && 'w-full',
          {
            positive: 'PrimaryCta--positive',
            neutral: 'PrimaryCta--neutral',
            default: undefined,
          }[variant],
          className,
        )}
      >
        {children}
      </div>
    </PrimaryCtaContext>
  );
};

export type PrimaryCtaSlotProps = PropsWithChildren;

export type PrimaryCtaIconChild =
  | ReactElement<PictogramProps, typeof Pictogram>
  | ReactElement<CommitmentAvatarProps, typeof CommitmentAvatar>;

export type PrimaryCtaIconProps = Omit<
  HTMLAttributes<HTMLDivElement>,
  'aria-hidden' | 'children'
> & {
  children: PrimaryCtaIconChild;
};

export const PrimaryCtaIcon: React.FC<PrimaryCtaIconProps> = ({
  children,
  className,
  ...props
}) => (
  <div
    {...props}
    aria-hidden="true"
    className={cn('PrimaryCta__icon', className)}
  >
    {children}
  </div>
);

export const PrimaryCtaBody: React.FC<PrimaryCtaSlotProps> = ({ children }) => (
  <div className="PrimaryCta__body flex flex-col gap-snug-md md:flex-1">
    {children}
  </div>
);

export type PrimaryCtaTextProps = PropsWithChildren<
  HTMLAttributes<HTMLDivElement>
>;

export type PrimaryCtaHeadingProps = PropsWithChildren<
  Omit<HTMLAttributes<HTMLDivElement>, 'id'>
>;

export const PrimaryCtaHeading: React.FC<PrimaryCtaHeadingProps> = ({
  className,
  children,
  ...props
}) => {
  const { headingId, size } = usePrimaryCtaContext();

  return (
    <div
      {...props}
      id={headingId}
      className={cn(
        'PrimaryCta__heading label-potent label-neutral-amplified max-w-sm',
        {
          sm: 'label-lg',
          md: 'label-lg md:label-xl md:label-potent',
        }[size],
        className,
      )}
    >
      {children}
    </div>
  );
};

export const PrimaryCtaText: React.FC<PrimaryCtaTextProps> = ({
  className,
  children,
  ...props
}) => {
  const { size } = usePrimaryCtaContext();

  return (
    <div
      {...props}
      className={cn(
        'label-moderate label-neutral-mild max-w-sm',
        {
          sm: 'label-md',
          md: 'label-md md:label-lg md:label-moderate',
        }[size],
        className,
      )}
    >
      {children}
    </div>
  );
};

export type PrimaryCtaActionsProps = PropsWithChildren<{
  reverseOnDesktop?: boolean;
}>;

export const PrimaryCtaActions: React.FC<PrimaryCtaActionsProps> = ({
  reverseOnDesktop = false,
  children,
}) => (
  <div
    className={cn(
      'PrimaryCta__actions flex gap-snug-lg md:pl-comfy-lg md:ml-auto',
      reverseOnDesktop && 'md:flex-row-reverse',
    )}
  >
    {children}
  </div>
);
